/**
 * Targeted /api/cta-flags fetch for the visitor's exact geo.
 * Re-fetches when geo-scoped admin revision matches this session (not a global reload).
 */

import { useCallback, useEffect, useState } from "react";
import {
  buildCtaWatchScopes,
  buildMejoresAmigosWatchScopes,
  filterRevisionForSession,
  GEO_CONFIG_REVISION_EVENT,
  type GeoRevisionEventDetail,
} from "@/lib/geoConfigRevision";
import {
  getServerTemplateConfig,
  type ServerTemplateConfig,
} from "@/lib/serverTemplateDetection";
import { appendGeoSimParamsToSearch } from "@/lib/geoSim";

export interface FreshCtaFlags {
  hideCta: boolean;
  showCheckout: boolean;
  showCommunity: boolean;
  showBirthday: boolean;
  showWishlist: boolean;
  showBestFriends: boolean;
  ctaUrl?: string;
  ctaLabel?: string;
  resolved?: string;
  source?: string;
  mejoresAmigosTiers?: {
    medium?: { url: string; label: string };
    high?: { url: string; label: string };
  };
  geo?: {
    betaLabel?: string | null;
    matchedCity?: string;
  };
}

function buildCtaFlagsParams(
  countryCode: string,
  templateConfig: ServerTemplateConfig | null | undefined,
): URLSearchParams {
  const params = new URLSearchParams();
  params.set("country", countryCode || "AR");
  const province = templateConfig?.province;
  const city =
    templateConfig?.city ||
    templateConfig?.cfCity ||
    templateConfig?.neighborhoodCity ||
    null;
  const neighborhood = templateConfig?.neighborhood;
  if (province) params.set("province", province);
  if (city) params.set("city", city);
  if (neighborhood) params.set("neighborhood", neighborhood);
  // Geo-sim: reenviar gst/geo_sim para que el poll no pise con la geo real
  // del operador (edge honra los mismos params que el HTML inject).
  appendGeoSimParamsToSearch(params);
  return params;
}

/**
 * El fetch defensivo /api/cta-flags es la fuente de verdad post-admin-save.
 * Siempre aplicamos fresh cuando responde OK — el Worker HTML puede estar stale
 * (KV 60s, html fast-path) y el objetivo del hook es corregirlo sin reload.
 */
function freshFlagsContradictWorker(
  _cfg: ServerTemplateConfig | null | undefined,
  _fresh: FreshCtaFlags,
): boolean {
  return false;
}

export function useFreshCtaFlags(args: {
  enabled: boolean;
  /** Worker ya resolvió geo con CTA off — omitir fetch inicial; revision admin sigue activa. */
  skipInitialFetch?: boolean;
  countryCode: string;
  templateConfig: ServerTemplateConfig | null | undefined;
}): { freshFlags: FreshCtaFlags | null; isFreshLoading: boolean } {
  const { enabled, skipInitialFetch = false, countryCode, templateConfig } = args;
  const [freshFlags, setFreshFlags] = useState<FreshCtaFlags | null>(null);
  const [isFreshLoading, setIsFreshLoading] = useState(false);
  const [fetchGen, setFetchGen] = useState(0);

  const runFetch = useCallback(async (signal?: AbortSignal) => {
    const params = buildCtaFlagsParams(countryCode, templateConfig);
    setIsFreshLoading(true);
    try {
      const r = await fetch(`/api/cta-flags?${params.toString()}`, {
        cache: "no-cache",
        signal,
      });
      if (!r.ok) return;
      const data = (await r.json()) as Partial<FreshCtaFlags> | null;
      if (!data) return;
      const next: FreshCtaFlags = {
        hideCta: !!data.hideCta,
        showCheckout: !!data.showCheckout,
        showCommunity: !!data.showCommunity,
        showBirthday: !!data.showBirthday,
        showWishlist: !!data.showWishlist,
        showBestFriends: !!data.showBestFriends,
        ctaUrl: data.ctaUrl || undefined,
        ctaLabel: data.ctaLabel || undefined,
        resolved: data.resolved || undefined,
        source: data.source || undefined,
        mejoresAmigosTiers: data.mejoresAmigosTiers,
        geo: data.geo,
      };
      if (freshFlagsContradictWorker(templateConfig, next)) return;
      setFreshFlags(next);
    } catch {
      /* keep prior flags / HTML injection */
    } finally {
      setIsFreshLoading(false);
    }
  }, [countryCode, templateConfig]);

  useEffect(() => {
    if (!enabled) return;
    if (skipInitialFetch && fetchGen === 0) return;
    const controller = new AbortController();
    void runFetch(controller.signal);
    return () => controller.abort();
  }, [
    enabled,
    skipInitialFetch,
    runFetch,
    fetchGen,
    countryCode,
    templateConfig?.province,
    templateConfig?.city,
    templateConfig?.cfCity,
    templateConfig?.neighborhood,
    templateConfig?.neighborhoodCity,
  ]);

  useEffect(() => {
    if (!enabled) return;
    const onRevision = (e: Event) => {
      const raw = (e as CustomEvent<GeoRevisionEventDetail>).detail;
      if (!raw?.bumpedScopes?.length) return;
      const cfg = getServerTemplateConfig();
      const watchKeys = [
        ...new Set([
          ...buildCtaWatchScopes(cfg),
          ...buildMejoresAmigosWatchScopes(cfg),
        ]),
      ];
      const detail = filterRevisionForSession(raw, watchKeys);
      if (!detail?.kinds?.length) return;
      if (
        detail.kinds.includes("cta") ||
        detail.kinds.includes("neighborhood") ||
        detail.kinds.includes("mejores_amigos")
      ) {
        setFreshFlags(null);
        setFetchGen((g) => g + 1);
      }
    };
    window.addEventListener(GEO_CONFIG_REVISION_EVENT, onRevision);
    return () => window.removeEventListener(GEO_CONFIG_REVISION_EVENT, onRevision);
  }, [enabled]);

  return { freshFlags, isFreshLoading };
}